import React from 'react';

import {IconChevronDown} from '@tabler/icons-react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';

import {dimensions} from '@stryd/react-ui';

import {usePageRegionData} from 'src/contexts';
import {VariantPricing} from 'src/features/pricing/helpers';
import {MoneyBag} from 'src/features/pricing/types';
import {usePriceFormatters} from 'src/hooks/usePriceFormatters';

const columnLayoutBreakpoint = dimensions.breakpoints.tabletLandscape;

interface CheckoutSummaryProps {
  onCheckout: () => void;
  shippingTimeline?: string;
  productPrices: {
    [key: string]: {
      [key: string]: VariantPricing;
    };
  };
  selectedProduct: string;
  selectedCommitment: string;
  includeClips: boolean;
  clipPricing: VariantPricing;
  clipOriginalPricing: VariantPricing;
  productName: string;
  className?: string;
}

const CheckoutContainer = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: #1a1a1a;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: stretch;
  z-index: 100;
  border-top: 1px solid #333;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    position: sticky;
    background: #1a1a1a;
    margin-top: auto;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0;
    padding: 20px;
  }
`;

const PriceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const CurrentPrice = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
`;

const OriginalPrice = styled.span`
  font-size: 18px;
  font-weight: 400;
  color: #888;
  text-decoration: line-through;
  line-height: 1;
`;

const PriceBreakdown = styled.div`
  display: flex;
  gap: 2px;
  margin-left: 0;
  flex-direction: column;
  align-items: flex-start;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    margin-left: 16px;
  }
`;

const PriceItem = styled.span`
  font-size: 14px;
  color: #ccc;
  font-weight: 500;
`;

const PlusSign = styled.span`
  font-size: 14px;
  color: #888;
  font-weight: 400;
`;

const PlanInfo = styled.div`
  font-size: 12px;
  color: #888;
  margin-top: 4px;
`;

const DropdownIcon = styled.div`
  color: #888;
  cursor: pointer;
  margin-left: 8px;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(1px);
  }
`;

const ActionSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    align-items: flex-end;
    min-width: 200px;
  }
`;

const CheckoutButton = styled.button`
  background: #fff;
  color: #000;
  border: none;
  border-radius: 8px;
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 140px;

  &:hover {
    background: #f0f0f0;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #666;
    color: #999;
    cursor: not-allowed;
    transform: none;
  }
`;

const ShippingInfo = styled.div`
  font-size: 12px;
  color: #888;
  text-align: center;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    text-align: right;
  }
`;

const CheckoutSummary: React.FC<
  React.PropsWithChildren<CheckoutSummaryProps>
> = ({
  onCheckout,
  shippingTimeline = 'Shipping now',
  productPrices,
  selectedProduct,
  selectedCommitment,
  includeClips,
  clipPricing,
  clipOriginalPricing,
  productName,
  className,
}) => {
  const {locale} = usePageRegionData();
  const {formatOneTimePrice} = usePriceFormatters({
    locale,
  });
  const intl = useIntl();
  // get the product price object
  const productPriceObject = productPrices[selectedProduct][selectedCommitment];

  // let clipMultiplier = 1;
  // set the clips price
  let clipsPrice = 0;
  let clipsOriginalPrice = 0;
  if (includeClips) {
    clipsPrice = clipPricing.oneTimePricesTotal.unit_amount;
    clipsOriginalPrice = clipOriginalPricing.oneTimePricesTotal.unit_amount;
  }

  // set the formatted total
  const formatterTotalMB: MoneyBag = {
    ...productPriceObject.oneTimePricesTotal,
    unit_amount: productPriceObject.oneTimePricesTotal.unit_amount + clipsPrice,
  };
  let formattedTotal = formatOneTimePrice(formatterTotalMB);

  const noMembershipProduct = productPrices[selectedProduct]['no-membership'];
  const monthlyProduct = productPrices[selectedProduct]['monthly-commitment'];
  const yearlyProduct = productPrices[selectedProduct]['yearly-upfront'];

  // update the total with the commitment price
  if (
    selectedCommitment === 'monthly-commitment' ||
    selectedCommitment === 'yearly-upfront'
  ) {
    const priceDifferenceMonthly: MoneyBag = {
      ...productPriceObject.oneTimePricesTotal,
      unit_amount:
        productPriceObject.oneTimePricesTotal.unit_amount +
        productPriceObject.recurringPricesTotals[0].unit_amount +
        clipsPrice,
    };
    formattedTotal = formatOneTimePrice(priceDifferenceMonthly);
  }

  // set the original price
  let formattedOriginal = '';
  if (selectedCommitment === 'monthly-commitment') {
    const priceDifferenceMonthly: MoneyBag = {
      ...noMembershipProduct.oneTimePricesTotal,
      unit_amount:
        noMembershipProduct.oneTimePricesTotal.unit_amount +
        clipsOriginalPrice +
        monthlyProduct.recurringPricesTotals[0].unit_amount,
    };
    formattedOriginal = formatOneTimePrice(priceDifferenceMonthly);
  } else if (selectedCommitment === 'yearly-upfront') {
    const yearlyPriceRecurringSavings =
      monthlyProduct.recurringPricesTotals[0].unit_amount * 12 -
      yearlyProduct.recurringPricesTotals[0].unit_amount;
    const yearlyPriceRecurringSavingsRounded =
      Math.trunc(yearlyPriceRecurringSavings / 100) * 100;

    const priceDifferenceYearly: MoneyBag = {
      ...noMembershipProduct.oneTimePricesTotal,
      unit_amount:
        noMembershipProduct.oneTimePricesTotal.unit_amount +
        productPriceObject.recurringPricesTotals[0].unit_amount +
        clipsOriginalPrice +
        yearlyPriceRecurringSavingsRounded,
    };
    formattedOriginal = formatOneTimePrice(priceDifferenceYearly);
  }
  const productPrice = formatOneTimePrice(
    productPriceObject.oneTimePricesTotal
  );

  const productPriceDescription = intl.formatMessage(
    {id: 'store5.checkout.strydPrice'},
    {
      price: productPrice,
      productName: productName,
    }
  );

  // set the commitment price
  let commitmentPrice = '';
  if (selectedCommitment === 'monthly-commitment') {
    commitmentPrice = intl.formatMessage(
      {id: 'store5.checkout.commitmentPrice'},
      {
        price: formatOneTimePrice(productPriceObject.recurringPricesTotals[0]),
        timeSpan: 'month',
      }
    );
  } else if (selectedCommitment === 'yearly-upfront') {
    commitmentPrice = intl.formatMessage(
      {id: 'store5.checkout.commitmentPrice'},
      {
        price: formatOneTimePrice(productPriceObject.recurringPricesTotals[0]),
        timeSpan: 'year',
      }
    );
  }

  let clipsPriceDescription = '';
  if (includeClips) {
    const clipPriceFormatted = formatOneTimePrice(
      clipPricing.oneTimePricesTotal
    );
    clipsPriceDescription = intl.formatMessage(
      {id: 'store5.checkout.clipsPrice'},
      {
        price: clipPriceFormatted,
      }
    );
  }

  return (
    <CheckoutContainer className={className}>
      <PriceSection>
        <PriceContainer>
          <CurrentPrice>{formattedTotal}</CurrentPrice>
          {formattedOriginal != '' && (
            <OriginalPrice>{formattedOriginal}</OriginalPrice>
          )}
        </PriceContainer>

        <PriceBreakdown>
          <PriceItem>{productPriceDescription}</PriceItem>
          {/* {commitmentPrice != '' && <PlusSign>+</PlusSign>} */}
          {commitmentPrice != '' && <PriceItem>+ {commitmentPrice}</PriceItem>}
          {/* {includeClips && <PlusSign>+</PlusSign>} */}
          {includeClips && <PriceItem>+ {clipsPriceDescription}</PriceItem>}
          {/* <DropdownIcon>
            <IconChevronDown size={16} />
          </DropdownIcon> */}
        </PriceBreakdown>

        <PlanInfo></PlanInfo>
      </PriceSection>

      <ActionSection>
        <CheckoutButton onClick={onCheckout}>
          {intl.formatMessage({id: 'store5.checkoutButton'})}
        </CheckoutButton>
        <ShippingInfo>{shippingTimeline}</ShippingInfo>
      </ActionSection>
    </CheckoutContainer>
  );
};

export default CheckoutSummary;
